import { useCallback, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import classNames from "classnames";
import { Camera, ChevronDown, Send, X } from "lucide-react";
import { backendLinkSuggestion } from "../config";
import {
  styles,
  Drawer,
  ErrorBox,
  Field,
  PageShell,
  Question,
  Reveal,
  Select,
  Sheet,
  asFile,
  localized,
  readImages,
  toDigits,
  useDrawers,
  usePhotoSource,
  cleanText,
} from "../components/wizard";

// one photo (client request), compressed like the complaint photos before it is sent
const MAX_PHOTOS = 1;

const STEP_LABELS = {
  type: "sgStType",
  subtype: "sgStSub",
  brand: "sgStMarque",
  segment: "suggestionStepSegment",
};

// Types whose name is "Autre" / "Other" / "أخرى" (any case) skip straight to the form
const isOtherType = (type) =>
  ["autre", "other", "أخرى"].some((v) =>
    [type?.name, type?.arabic_translation, type?.english_translation].filter(Boolean).some((n) => n.toLowerCase() === v)
  );

// ── UI components ──────────────────────────────────────────────────────────────

function StepsBar({ steps, current }) {
  const { t } = useTranslation();
  const currentIdx = current ? steps.indexOf(current) : steps.length;
  return (
    <div className={styles.stepsBar} aria-hidden="true">
      {steps.map((step, i) => (
        <div className={styles.stepItem} key={step}>
          <div className={classNames(styles.stepDot, { [styles.done]: i < currentIdx, [styles.active]: i === currentIdx })}>
            {i < currentIdx ? "✓" : i + 1}
          </div>
          {i === currentIdx && <span className={styles.stepLabel}>{t(STEP_LABELS[step])}</span>}
          {i < steps.length - 1 && <div className={classNames(styles.stepLine, { [styles.done]: i < currentIdx })} />}
        </div>
      ))}
    </div>
  );
}

function IconGrid({ items, selectedId, onSelect, cols4 }) {
  return (
    <div className={classNames(styles.iconGrid, { [styles.cols4]: cols4 })}>
      {items.map((item) => (
        <button
          type="button"
          key={item.id}
          aria-pressed={item.id === selectedId}
          className={classNames(styles.igBtn, { [styles.isSelected]: item.id === selectedId })}
          onClick={() => onSelect(item.id)}
        >
          <span className={styles.igIcon} aria-hidden="true">
            {item.icon}
          </span>
          <span className={styles.igLabel}>{item.label}</span>
        </button>
      ))}
    </div>
  );
}

// null = loading  |  false = error  |  array = data
function Loadable({ data, children }) {
  const { t } = useTranslation();
  if (data === null) return <p className={styles.statusText}>{t("suggestionLoading")}</p>;
  if (data === false) return <p className={styles.statusText}>{t("suggestionLoadError")}</p>;
  return children;
}

// "16 · Alger" in the current language
const regionLabel = (region, lang) => `${String(region.id).padStart(2, "0")} · ${localized(region, lang)}`;

// Wilayas: one is picked (client request). No search box: on a phone its keyboard hid the list.
function RegionsSheet({ regions, selectedId, onPick, onClose }) {
  const { t, i18n } = useTranslation();
  return (
    <Sheet tall title={t("suggestionWilayas")} onClose={onClose}>
      <div className={styles.plList}>
        {regions.map((r) => (
          <button
            type="button"
            key={r.id}
            className={classNames(styles.plRow, { [styles.isCurrent]: r.id === selectedId })}
            aria-pressed={r.id === selectedId}
            onClick={() => onPick(r.id)}
          >
            <span className={styles.plName}>{regionLabel(r, i18n.language)}</span>
          </button>
        ))}
      </div>
    </Sheet>
  );
}

// Thank-you popup after sending (client's animated model, popup_merci_anime.html): confetti, a check
// that draws itself, "Merci !" letter by letter, then it closes by itself after 5 s.
const THANKS_MS = 5000;
const CONFETTI_COLORS = ["#378ADD", "#1D9E75", "#EF9F27", "#D4537E", "#7F77DD", "#E24B4A"];

function SuggestionThanks({ onDone }) {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";
  const [closing, setClosing] = useState(false);
  const big = t("sgThanksBig");
  // letters bounce in one by one; Arabic bounces in as one piece (split, its letters would no
  // longer join and the words and "!" would lose their right-to-left order)
  const parts = isAr ? [big] : big.split("");
  const confetti = useMemo(
    () =>
      Array.from({ length: 32 }, (_, i) => {
        const angle = Math.random() * Math.PI * 2;
        const radius = 90 + Math.random() * 130;
        return {
          "--dx": `${Math.round(Math.cos(angle) * radius)}px`,
          "--dy": `${Math.round(Math.sin(angle) * radius * 0.8 + 70)}px`,
          "--r": `${Math.round(Math.random() * 720 - 360)}deg`,
          background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
          animationDelay: `${(0.35 + Math.random() * 0.15).toFixed(2)}s`,
        };
      }),
    []
  );

  useEffect(() => {
    const fade = setTimeout(() => setClosing(true), THANKS_MS);
    const leave = setTimeout(onDone, THANKS_MS + 400);
    return () => {
      clearTimeout(fade);
      clearTimeout(leave);
    };
  }, [onDone]);

  // fixed over the whole screen; rendered inside the page so it keeps the client's colours
  return (
    <div className={styles.thxOverlay} onClick={onDone} role="status" aria-live="polite">
      <div className={classNames(styles.thxCard, { [styles.thxClosing]: closing })} dir={isAr ? "rtl" : "ltr"}>
        <div className={styles.thxConfetti} aria-hidden="true">
          {confetti.map((style, i) => (
            <span key={i} style={style} />
          ))}
        </div>
        <div className={styles.thxRing}>
          <svg width="42" height="42" viewBox="0 0 36 36" fill="none" aria-hidden="true">
            <path d="M8 19l7 7 13-14" />
          </svg>
        </div>
        <div className={styles.thxBig}>
          {parts.map((part, i) => (
            <span key={i} style={{ animationDelay: `${(0.5 + i * 0.07).toFixed(2)}s` }}>
              {part === " " ? "\u00a0" : part}
            </span>
          ))}
        </div>
        <p className={styles.thxText}>{t("sgThanksText")}</p>
        <div className={styles.thxTrack}>
          <div className={styles.thxBar} style={{ animationDuration: `${THANKS_MS}ms` }} />
        </div>
        <p className={styles.thxAuto}>{t("sgThanksAuto")}</p>
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

// Consumer suggestion: type → subtype → brand (+ segment) → form.
// The brand and segment steps only exist when their key is in the chosen subtype's `fields`.
export default function SuggestionForm({ currentLang, setCurrentLang }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const goHome = useCallback(() => navigate("/"), [navigate]);

  // null = loading  |  false = error  |  array = data
  const [types, setTypes] = useState(null);
  useEffect(() => {
    axios
      .get(`${backendLinkSuggestion}getSuggestionTypes`, { withCredentials: true })
      .then((res) => setTypes(res.data?.data || []))
      .catch(() => setTypes(false));
  }, []);

  // Catalog data fetched per step
  const [brands, setBrands] = useState(null);
  const [segments, setSegments] = useState(null);
  const [products, setProducts] = useState(null);
  const [regions, setRegions] = useState(null);

  // Step selections
  const [selectedType, setSelectedType] = useState(null);
  const [selectedSubtype, setSelectedSubtype] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [segmentSheetOpen, setSegmentSheetOpen] = useState(false);

  // Form state
  const [productId, setProductId] = useState("");
  const [regionId, setRegionId] = useState(null); // the one wilaya picked
  const [regionsSheetOpen, setRegionsSheetOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [rating, setRating] = useState(0);
  const [photos, setPhotos] = useState([]); // [{ id, url, file }]
  const [consent, setConsent] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const fields = selectedSubtype?.fields || {};
  const hasSubtypes = selectedType?.subtypes?.length > 0 && !isOtherType(selectedType);
  const steps = [
    "type",
    hasSubtypes && "subtype",
    "brand_id" in fields && "brand", // the segment, when asked, is chosen in a sheet right after the brand
    !("brand_id" in fields) && "segment_id" in fields && "segment",
  ].filter(Boolean);
  const drawers = useDrawers(steps);
  const formReady = Array.isArray(types) && !drawers.firstTodo;

  // ── Fetches triggered by step ────────────────────────────────────────────────

  // Fetch brands when a subtype with brand_id in fields is selected
  useEffect(() => {
    if (!selectedSubtype || !("brand_id" in (selectedSubtype.fields || {}))) return;
    setBrands(null);
    axios
      .get(`${backendLinkSuggestion}getBrands`, { withCredentials: true })
      .then((res) => setBrands(res.data?.data || []))
      .catch(() => setBrands(false));
  }, [selectedSubtype]);

  // Fetch segments: brand_id in fields → wait for brand, pass brand_id
  useEffect(() => {
    const f = selectedSubtype?.fields || {};
    if (!selectedSubtype || !("segment_id" in f)) return;
    if ("brand_id" in f && !selectedBrand) return;
    setSegments(null);
    const params = selectedBrand ? { brand_id: selectedBrand.id } : {};
    axios
      .get(`${backendLinkSuggestion}getSegments`, { withCredentials: true, params })
      .then((res) => setSegments(res.data?.data || []))
      .catch(() => setSegments(false));
  }, [selectedSubtype, selectedBrand]);

  // Fetch products + regions when the form is reached
  useEffect(() => {
    if (!formReady) return;
    const f = selectedSubtype?.fields || {};
    if ("product_id" in f) {
      setProducts(null);
      const params = {};
      if (selectedBrand) params.brand_id = selectedBrand.id;
      if (selectedSegment) params.segment_id = selectedSegment.id;
      axios
        .get(`${backendLinkSuggestion}getProducts`, { withCredentials: true, params })
        .then((res) => setProducts(res.data?.data || []))
        .catch(() => setProducts(false));
    }
    if ("willaya_id" in f) {
      setRegions(null);
      axios
        .get(`${backendLinkSuggestion}getRegions`, { withCredentials: true })
        .then((res) => setRegions(res.data?.data || []))
        .catch(() => setRegions(false));
    }
  }, [formReady, selectedSubtype, selectedBrand, selectedSegment]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  // What the form asks depends on the choices above: it is emptied when one of them changes
  const resetForm = () => {
    photos.forEach((p) => URL.revokeObjectURL(p.url));
    setProductId("");
    setRegionId(null);
    setComment("");
    setRating(0);
    setPhotos([]);
    setConsent(false);
    setName("");
    setPhone("");
    setError("");
    setProducts(null);
    setRegions(null);
  };

  // Choosing a step answer resets all the selections that come after it
  const pickType = (type) => {
    setSelectedType(type);
    setSelectedSubtype(null);
    setSelectedBrand(null);
    setSelectedSegment(null);
    setBrands(null);
    setSegments(null);
    resetForm();
    drawers.reset("subtype", "brand", "segment");
    drawers.close("type", true);
  };

  const pickSubtype = (subtype) => {
    setSelectedSubtype(subtype);
    setSelectedBrand(null);
    setSelectedSegment(null);
    setBrands(null);
    setSegments(null);
    resetForm();
    drawers.reset("brand", "segment");
    drawers.close("subtype", true);
  };

  const pickBrand = (brand) => {
    setSelectedBrand(brand);
    setSelectedSegment(null);
    resetForm();
    if ("segment_id" in fields) {
      // not filled until the segment is chosen too
      drawers.reset("brand");
      setSegmentSheetOpen(true);
    } else {
      drawers.close("brand", true);
    }
  };

  const pickSegment = (segment) => {
    setSelectedSegment(segment);
    setSegmentSheetOpen(false);
    resetForm();
    drawers.close("brand_id" in fields ? "brand" : "segment", true);
  };

  const pickRegion = (id) => {
    setRegionId(id);
    setRegionsSheetOpen(false);
  };

  // compressed like the complaint photos: a phone photo of several MB made sending slow
  const [photoError, setPhotoError] = useState("");
  const addPhotos = async (e) => {
    const { items, error: readError } = await readImages(e.target, MAX_PHOTOS - photos.length);
    setPhotoError(readError ? t(readError) : "");
    const added = items.map((item) => ({ ...item, id: `${item.file.name}-${Date.now()}-${Math.random().toString(36).slice(2)}` }));
    setPhotos((prev) => [...prev, ...added].slice(0, MAX_PHOTOS));
  };
  // "take a photo" or "choose a photo", as in the complaint form (Android only offered the gallery)
  const photoSource = usePhotoSource({ multiple: false, onChange: addPhotos });

  const removePhoto = (id) => {
    const photo = photos.find((p) => p.id === id);
    if (photo) URL.revokeObjectURL(photo.url);
    setPhotos((prev) => prev.filter((p) => p.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending || !formReady) return;
    setSending(true);
    setError("");

    const formData = new FormData();
    formData.append("suggestion_type_id", selectedType.id);
    if (selectedSubtype) formData.append("suggestion_subtype_id", selectedSubtype.id);
    if (selectedBrand) formData.append("brand_id", selectedBrand.id);
    if (selectedSegment) formData.append("segment_id", selectedSegment.id);
    if (productId) formData.append("product_id", productId);
    if (regionId) formData.append("regions[]", regionId);
    if (comment.trim()) formData.append("comment", comment.trim());
    if (rating > 0) formData.append("rating", rating);
    photos.forEach((p) => formData.append("img_s[]", asFile(p)));
    formData.append("contact_consent", consent ? "1" : "0");
    if (consent) {
      formData.append("full_name", name.trim());
      formData.append("phone", phone.trim());
    }

    try {
      await axios.post(`${backendLinkSuggestion}addSuggestion`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitted(true);
    } catch {
      setError(t("suggestionErrorServer"));
    } finally {
      setSending(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const segmentItems = useMemo(
    () => (Array.isArray(segments) ? segments.map((s) => ({ id: s.id, label: localized(s, lang), icon: s.icon || "🗂️" })) : []),
    [segments, lang]
  );
  const drawerProps = (step) => ({ drawers, step, sub: t(STEP_LABELS[step]) });

  return (
    <PageShell
      title={t("sgSuggestion")}
      onBack={goHome}
      currentLang={currentLang}
      setCurrentLang={setCurrentLang}
      below={
        <>
          {segmentSheetOpen && (
            <Sheet
              title={<Trans i18nKey="sgSheetSegment" values={{ brand: selectedBrand?.name }} components={{ b: <b /> }} />}
              onClose={() => setSegmentSheetOpen(false)}
            >
              <Loadable data={segments}>
                <IconGrid items={segmentItems} selectedId={selectedSegment?.id ?? null} onSelect={(id) => pickSegment(segments.find((s) => s.id === id))} />
              </Loadable>
            </Sheet>
          )}
          {regionsSheetOpen && Array.isArray(regions) && (
            <RegionsSheet regions={regions} selectedId={regionId} onPick={pickRegion} onClose={() => setRegionsSheetOpen(false)} />
          )}
          {photoSource.element}
          {/* after sending: the animated thank-you, then back to the home page */}
          {submitted && <SuggestionThanks onDone={goHome} />}
        </>
      }
    >
      {Array.isArray(types) && <StepsBar steps={steps} current={drawers.firstTodo} />}

      <div className={styles.cardBody}>
        <Loadable data={types}>
          <form noValidate onSubmit={handleSubmit}>
            <Drawer {...drawerProps("type")} icon={selectedType?.icon || "💡"} summary={selectedType && localized(selectedType, lang)}>
              <Question i18nKey="sgQType" />
              <IconGrid
                items={(types || []).map((type) => ({ id: type.id, label: localized(type, lang), icon: type.icon || "💡" }))}
                selectedId={selectedType?.id ?? null}
                onSelect={(id) => pickType(types.find((type) => type.id === id))}
              />
            </Drawer>

            {steps.includes("subtype") && (
              <Drawer {...drawerProps("subtype")} icon="📌" summary={selectedSubtype && localized(selectedSubtype, lang)}>
                <Question i18nKey="sgQSub" />
                <div className={styles.chips}>
                  {selectedType.subtypes.map((subtype) => (
                    <button
                      type="button"
                      key={subtype.id}
                      aria-pressed={subtype.id === selectedSubtype?.id}
                      className={classNames(styles.chip, { [styles.isSelected]: subtype.id === selectedSubtype?.id })}
                      onClick={() => pickSubtype(subtype)}
                    >
                      {localized(subtype, lang)}
                    </button>
                  ))}
                </div>
              </Drawer>
            )}

            {steps.includes("brand") && (
              <Drawer
                {...drawerProps("brand")}
                icon="🏷️"
                summary={[selectedBrand?.name, selectedSegment && localized(selectedSegment, lang)].filter(Boolean).join(" — ")}
              >
                <Question i18nKey="sgQMarque" />
                <Loadable data={brands}>
                  <IconGrid
                    items={(brands || []).map((brand) => ({ id: brand.id, label: brand.name, icon: "🏷️" }))}
                    selectedId={selectedBrand?.id ?? null}
                    onSelect={(id) => pickBrand(brands.find((brand) => brand.id === id))}
                  />
                </Loadable>
              </Drawer>
            )}

            {steps.includes("segment") && (
              <Drawer {...drawerProps("segment")} icon={selectedSegment?.icon || "🗂️"} summary={selectedSegment && localized(selectedSegment, lang)}>
                <Question i18nKey="suggestionQuestionSegment" />
                <Loadable data={segments}>
                  <IconGrid items={segmentItems} selectedId={selectedSegment?.id ?? null} onSelect={(id) => pickSegment(segments.find((s) => s.id === id))} />
                </Loadable>
              </Drawer>
            )}

            {/* The form: shown once every step is filled */}
            <Reveal open={formReady}>
              {"product_id" in fields && (
                <Field label={t("sgProductLabel")}>
                  <Loadable data={products}>
                    <Select
                      value={productId}
                      placeholder={t("chooseProduct")}
                      options={(products || []).map((p) => ({ value: String(p.id), label: localized(p, lang) }))}
                      onChange={setProductId}
                    />
                  </Loadable>
                </Field>
              )}

              {"willaya_id" in fields && (
                <div className={styles.field}>
                  <span className={styles.fieldLabel}>{t("sgWilayaAvail")}</span>
                  <Loadable data={regions}>
                    <button
                      type="button"
                      className={classNames(styles.ghost, styles.block, { [styles.isOn]: regionId })}
                      style={{ justifyContent: "space-between" }}
                      onClick={() => setRegionsSheetOpen(true)}
                    >
                      <span>
                        {regionId && Array.isArray(regions)
                          ? regionLabel(regions.find((r) => r.id === regionId) || { id: regionId }, lang)
                          : t("rcWilayaPh")}
                      </span>
                      <ChevronDown size={16} aria-hidden />
                    </button>
                  </Loadable>
                </div>
              )}

              <div className={styles.field} style={{ marginTop: 6 }}>
                <label className={styles.fieldLabel} htmlFor="sgComment">
                  {t("sgComment")}
                </label>
                <textarea
                  id="sgComment"
                  className={styles.textarea}
                  maxLength={500}
                  placeholder={t("sgCommentPh")}
                  value={comment}
                  onChange={(e) => setComment(cleanText(e.target.value))}
                />
                <div className={styles.count}>{comment.length} / 500</div>
                <div className={styles.mediaRow}>
                  <button
                    type="button"
                    className={classNames(styles.ghost, { [styles.isDisabled]: photos.length >= MAX_PHOTOS })}
                    disabled={photos.length >= MAX_PHOTOS}
                    onClick={photoSource.openSource}
                  >
                    <Camera size={18} strokeWidth={1.8} aria-hidden />
                    <span>{t("rcAddPhotos")}</span>
                  </button>
                </div>
                {photos.length > 0 && (
                  <div className={styles.thumbs}>
                    {photos.map((p) => (
                      <div key={p.id} className={styles.thumb}>
                        <img src={p.url} alt="" />
                        <button type="button" className={styles.thumbRemove} aria-label={t("rcRemovePhoto")} onClick={() => removePhoto(p.id)}>
                          <X size={14} aria-hidden />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {photoError && <div className={classNames(styles.hint, styles.isBad)}>{photoError}</div>}
                <div className={styles.hint}>{t("sgPhotoLimit")}</div>
              </div>

              <div className={styles.field} style={{ marginBottom: 4 }}>
                <span className={styles.fieldLabel} style={{ marginBottom: 0 }}>
                  {t("sgRating")}
                </span>
                <div className={styles.starsRow}>
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      type="button"
                      key={value}
                      className={classNames(styles.star, { [styles.isFilled]: value <= rating })}
                      aria-label={`${value} / 5`}
                      aria-pressed={value === rating}
                      // tapping the current note again removes it
                      onClick={() => setRating(value === rating ? 0 : value)}
                    >
                      <svg viewBox="0 0 24 24" width="30" height="30" aria-hidden="true">
                        <path d="M12 2.5l2.95 6.28 6.93.62-5.24 4.62 1.57 6.79-6.21-3.72-6.21 3.72 1.57-6.79-5.24-4.62 6.93-.62z" />
                      </svg>
                    </button>
                  ))}
                  <span className={styles.ratingLbl}>{rating > 0 ? t(`sgRating${rating}`) : "—"}</span>
                </div>
              </div>

              {/* contact details only if the customer agrees */}
              <label className={styles.consentRow}>
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                <span className={styles.consentText}>{t("sgConsent")}</span>
              </label>
              {consent && (
                <div className={styles.twoCol} style={{ marginTop: 12 }}>
                  <Field label={t("rcName")}>
                    <input
                      type="text"
                      className={styles.input}
                      autoComplete="name"
                      placeholder={t("rcNamePh")}
                      value={name}
                      onChange={(e) => setName(cleanText(e.target.value).replace(/[0-9\u0660-\u0669,.!?()،؟]/g, ""))}
                    />
                  </Field>
                  <Field label={t("rcPhone")}>
                    <input
                      type="tel"
                      className={classNames(styles.input, styles.ltr)}
                      autoComplete="tel"
                      inputMode="numeric"
                      placeholder="0555123456"
                      value={phone}
                      onChange={(e) => setPhone(toDigits(e.target.value).slice(0, 10))}
                    />
                  </Field>
                </div>
              )}

              <ErrorBox>{error}</ErrorBox>
              <button type="submit" className={classNames(styles.submit, { [styles.isSuccess]: submitted })} disabled={sending || submitted}>
                <Send size={17} strokeWidth={2.2} aria-hidden />
                <span>{submitted ? t("rcSent") : sending ? t("suggestionSending") : t("sgSend")}</span>
              </button>
            </Reveal>
          </form>
        </Loadable>
      </div>
    </PageShell>
  );
}
